import { apm } from '@elastic/apm-rum';
import { useEffect, useState } from 'react';
import { type FallbackProps, getErrorMessage } from 'react-error-boundary';
import { v4 } from 'uuid';
import useUserOnlineStatus from '../../../../../hooks/useUserOnlineStatus';
import { ClarityEvents } from '../../../../../lib/clarity/types/clarityEvents.types';
import { captureEvent } from '../../../../../lib/clarity/utils/captureEvent.utils';
import {
  hasRecentAutoReloadAttempt,
  storeAutoReloadAttempt,
} from '../errorBoundaryFallbackPage.utils';

const errorMessagesToForceRestart = [
  'Failed to fetch dynamically imported module:',
  'is not a valid JavaScript MIME type',
  'Importing a module script failed.',
  'error loading dynamically imported module',
];

const isErrorMessageToForceRestart = (errorMessage: string) => {
  const normalizedErrorMessage = errorMessage.toLowerCase();

  return errorMessagesToForceRestart.some((forceRestartMessage) =>
    normalizedErrorMessage.includes(forceRestartMessage.toLowerCase()),
  );
};

export const useErrorBoundaryFallbackPage = ({ error }: Pick<FallbackProps, 'error'>) => {
  const [errorId, setErrorId] = useState<string | null>(null);
  const [isOutdatedProblemWithAutoReload, setIsOutdatedProblemWithAutoReload] = useState(false);

  const { isOnline, isLoadingOnlineStatus } = useUserOnlineStatus();

  const goToHomeWithReload = () => {
    window.location.pathname = '/';
  };

  const reloadPage = () => {
    window.location.reload();
  };

  const captureErrorInApmWithErrorId = () => {
    const errorId = v4();
    setErrorId(errorId);

    const capturedError =
      error instanceof Error ? error : (getErrorMessage(error) ?? String(error));

    apm.captureError(capturedError, { labels: { errorId } });
  };

  useEffect(() => {
    if (isLoadingOnlineStatus) {
      return;
    }

    const errorMessage = getErrorMessage(error);

    if (
      isOnline &&
      errorMessage &&
      isErrorMessageToForceRestart(errorMessage) &&
      !hasRecentAutoReloadAttempt()
    ) {
      captureEvent(ClarityEvents.AUTO_ASSETS_VERSION_UPGRADE);

      setIsOutdatedProblemWithAutoReload(true);

      storeAutoReloadAttempt();

      reloadPage();
    } else {
      captureEvent(ClarityEvents.ENTER_FALLBACK_ERROR_PAGE);

      captureErrorInApmWithErrorId();
    }
  }, [isOnline]);

  return {
    goToHomeWithReload,
    errorId: errorId?.slice(-10),
    isOutdatedProblemWithAutoReload,
    isValidatingOnlineStatus: isLoadingOnlineStatus,
  };
};

export default useErrorBoundaryFallbackPage;
